import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/cn'
import { copyText } from '@/lib/clipboard'
import { notifyError, notifySuccess } from '@/lib/notify'

interface ShareButtonProps {
  url: string
  className?: string
}

/**
 * Copies the giveaway's public link.
 *
 * Deliberately not the Web Share sheet: these links are pasted into Discord,
 * and going through the OS share picker adds a step to what should be one
 * click. The button confirms in place *and* raises a toast, so the feedback is
 * visible whether the reader is looking at the cursor or the page.
 */
export function ShareButton({ url, className }: ShareButtonProps) {
  const [copied, setCopied] = useState(false)
  const resetTimer = useRef<number | undefined>(undefined)

  useEffect(() => () => window.clearTimeout(resetTimer.current), [])

  const copy = async () => {
    if (!(await copyText(url))) {
      notifyError('Could not copy the link. Copy it from the address bar instead.')
      return
    }

    setCopied(true)
    notifySuccess('Link copied to clipboard')
    window.clearTimeout(resetTimer.current)
    resetTimer.current = window.setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      type="button"
      className={cn('action-btn', copied && 'is-done', className)}
      onClick={copy}
      aria-live="polite"
    >
      {copied ? (
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path d="M3 8.5 6.5 12 13 4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <rect x="5.5" y="5.5" width="8" height="8" rx="2" />
          <path d="M10.5 3.5A2 2 0 0 0 8.5 1.5h-5a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2" />
        </svg>
      )}
      {copied ? 'Link copied' : 'Copy link'}
    </button>
  )
}
